import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Video, Clock, Users, Search, Filter, Play, Download, Share2,
  Brain, Zap, AlertTriangle, FileText, CheckSquare, Loader2, Sparkles, X, 
  RefreshCw, ClipboardPaste, Lightbulb, Upload, Mic, FileAudio, XCircle, Lock,
  Wifi, Monitor, Mail
} from 'lucide-react';
import { useData } from '../../context/DataContext';
import { useAuth } from '../../context/AuthContext';
import { getPermissions } from '../../utils/permissions';
import { PipelineLiveView } from './PipelineLiveView';

const sampleTranscript = [
  { speaker: 'John Doe', time: '0:00', text: 'Good afternoon everyone. Let\'s start with the planning discussion.' },
  { speaker: 'Sarah Chen', time: '0:15', text: 'I have the budget projections ready. We\'re looking at a 15% increase in AI infrastructure costs.' },
  { speaker: 'Mike Torres', time: '0:45', text: 'That aligns with our expansion plans. The compliance module will need additional resources.' },
  { speaker: 'John Doe', time: '1:20', text: 'Agreed. Let\'s allocate additional budget for infrastructure upgrades.' },
  { speaker: 'Jennifer Walsh', time: '1:50', text: 'I\'ll need that documented for the audit trail. Also flagging a potential risk with the timeline.' },
];

const audioProcessingSteps = {
  uploading: { label: 'Uploading audio file...', step: 0 },
  transcribing: { label: 'Transcribing with Whisper AI...', step: 1 },
  analyzing: { label: 'Analyzing with GPT...', step: 2 },
  processed: { label: 'Complete!', step: 3 },
  failed: { label: 'Processing failed', step: -1 },
};

const textProcessingSteps = [
  'Analyzing meeting transcript...',
  'Extracting key topics...',
  'Identifying action items...',
  'Detecting risks...',
  'Generating summary...',
  'Finalizing extraction...',
];

const SUPPORTED_FORMATS = ['mp3', 'mp4', 'mpeg', 'mpga', 'm4a', 'wav', 'webm'];
const MAX_FILE_SIZE = 25 * 1024 * 1024;

function formatFileSize(bytes) {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

export const MeetingsScreen = () => {
  const { 
    meetings, processMeeting, processingMeetingId, getTasksForMeeting, getRisksForMeeting,
    uploadMeetingAudio, retranscribeMeeting, audioProcessingId, fetchAll
  } = useData();
  const { user, isDemo } = useAuth();
  const perms = getPermissions(user?.role);
  const [selectedMeetingId, setSelectedMeetingId] = useState(meetings[0]?.id);
  const [activeTab, setActiveTab] = useState('summary');
  const [searchQuery, setSearchQuery] = useState('');
  const [processingStep, setProcessingStep] = useState(0);
  const [transcriptInput, setTranscriptInput] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileError, setFileError] = useState('');
  const [inputMode, setInputMode] = useState('audio'); // 'audio' or 'text'
  const fileInputRef = useRef(null);

  // Simulation state
  const [simulating, setSimulating] = useState(false);
  const [simStage, setSimStage] = useState('');
  const [simMeetingId, setSimMeetingId] = useState(null);
  const [showPipeline, setShowPipeline] = useState(false);
  const [pipelineMeetingId, setPipelineMeetingId] = useState(null);
  const simPollRef = useRef(null);

  const selectedMeeting = meetings.find(m => m.id === selectedMeetingId) || meetings[0];
  const meetingTasks = selectedMeeting ? getTasksForMeeting(selectedMeeting.id) : [];
  const meetingRisks = selectedMeeting ? getRisksForMeeting(selectedMeeting.id) : [];
  const isTextProcessing = processingMeetingId === selectedMeeting?.id;
  const isAudioProcessing = audioProcessingId === selectedMeeting?.id;
  const isProcessing = isTextProcessing || isAudioProcessing || 
    ['uploading', 'transcribing', 'analyzing'].includes(selectedMeeting?.status);

  // Cycle through text processing steps
  useEffect(() => {
    if (!isTextProcessing) {
      setProcessingStep(0);
      return;
    }
    const interval = setInterval(() => {
      setProcessingStep(prev => (prev + 1) % textProcessingSteps.length);
    }, 350);
    return () => clearInterval(interval);
  }, [isTextProcessing]);

  const handleMeetingClick = async (meeting) => {
    setSelectedMeetingId(meeting.id);
    setActiveTab('summary');
    setTranscriptInput(meeting.transcript || '');
    setSelectedFile(null);
    setFileError('');
  };

  // ── Simulation (Demo Mode) ──────────────────────────────
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const simSources = [
    { id: 'zoom', label: 'Zoom Meeting', icon: Video, color: 'blue' },
    { id: 'teams', label: 'Teams Meeting', icon: Monitor, color: 'purple' },
    { id: 'google', label: 'Google Meet', icon: Wifi, color: 'green' },
    { id: 'email', label: 'Email Forward', icon: Mail, color: 'amber' },
  ];

  const stageLabels = {
    ingesting: 'Fetching recording from platform...',
    transcribing: 'Transcribing audio with Whisper AI...',
    analyzing: 'Extracting tasks, risks & decisions with GPT...',
    processed: 'Meeting processed successfully!',
    failed: 'Processing failed.',
  };

  const startSimulation = useCallback(async (source) => {
    if (simulating) return;
    setSimulating(true);
    setSimStage('ingesting');
    try {
      const token = localStorage.getItem('d3_token');
      const res = await fetch(`${API_URL}/api/ingest/simulate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ source }),
      });
      if (!res.ok) throw new Error('Simulation failed');
      const data = await res.json();
      setSimMeetingId(data.meeting_id);

      // Poll for status updates
      const poll = setInterval(async () => {
        try {
          const sRes = await fetch(`${API_URL}/api/ingest/status/${data.meeting_id}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (sRes.ok) {
            const status = await sRes.json();
            setSimStage(status.status);
            if (status.status === 'processed' || status.status === 'failed') {
              clearInterval(poll);
              // Refresh all data
              setTimeout(() => {
                fetchAll();
                setSimulating(false);
                setSimStage('');
                setSimMeetingId(null);
                setSelectedMeetingId(data.meeting_id);
              }, 1500);
            }
          }
        } catch { /* ignore poll errors */ }
      }, 800);
      simPollRef.current = poll;
    } catch {
      setSimulating(false);
      setSimStage('');
    }
  }, [simulating, API_URL, fetchAll]);

  useEffect(() => {
    return () => { if (simPollRef.current) clearInterval(simPollRef.current); };
  }, []);

  // "Watch Pipeline Live" — triggers simulation + opens overlay
  const startPipelineLive = useCallback(async () => {
    if (simulating) return;
    setSimulating(true);
    try {
      const token = localStorage.getItem('d3_token');
      const res = await fetch(`${API_URL}/api/ingest/simulate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ source: 'zoom' }),
      });
      if (!res.ok) throw new Error('Simulation failed');
      const data = await res.json();
      setPipelineMeetingId(data.meeting_id);
      setShowPipeline(true);
    } catch {
      setSimulating(false);
    }
  }, [simulating, API_URL]);

  const handlePipelineClose = useCallback(() => {
    setShowPipeline(false);
    setSimulating(false);
    fetchAll();
  }, [fetchAll]);

  const handlePipelineComplete = useCallback((meetingId) => {
    setShowPipeline(false);
    setSimulating(false);
    fetchAll();
    if (meetingId) {
      setTimeout(() => setSelectedMeetingId(meetingId), 500);
    }
  }, [fetchAll]);

  const handleProcess = async () => {
    if (!selectedMeeting || processingMeetingId !== null) return;
    await processMeeting(selectedMeeting.id, transcriptInput || undefined);
    setTranscriptInput('');
  };

  // Audio file validation
  const validateFile = (file) => {
    const ext = file.name.split('.').pop().toLowerCase();
    if (!SUPPORTED_FORMATS.includes(ext)) {
      return `Unsupported format. Supported: ${SUPPORTED_FORMATS.join(', ')}`;
    }
    if (file.size > MAX_FILE_SIZE) {
      return `File too large (${formatFileSize(file.size)}). Max 25MB.`;
    }
    return null;
  };

  const handleFileSelect = (file) => {
    setFileError('');
    const error = validateFile(file);
    if (error) {
      setFileError(error);
      setSelectedFile(null);
      return;
    }
    setSelectedFile(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const handleAudioUpload = async () => {
    if (!selectedMeeting || !selectedFile || isProcessing) return;
    await uploadMeetingAudio(selectedMeeting.id, selectedFile);
    setSelectedFile(null);
  };

  const handleRetranscribe = async () => {
    if (!selectedMeeting || isProcessing) return;
    await retranscribeMeeting(selectedMeeting.id);
  };

  const filteredMeetings = meetings.filter(m => 
    !searchQuery || m.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getStatusBadge = (meeting) => {
    const s = meeting.status || (meeting.processed ? 'processed' : 'pending');
    const badges = {
      processed: { cls: 'bg-green-500/20 text-green-400', text: 'AI Processed' },
      uploading: { cls: 'bg-blue-500/20 text-blue-400', text: 'Uploading', spin: true },
      ingesting: { cls: 'bg-indigo-500/20 text-indigo-400', text: 'Ingesting', spin: true },
      transcribing: { cls: 'bg-cyan-500/20 text-cyan-400', text: 'Transcribing', spin: true },
      analyzing: { cls: 'bg-purple-500/20 text-purple-400', text: 'Analyzing', spin: true },
      transcript_ready: { cls: 'bg-teal-500/20 text-teal-400', text: 'Transcript Ready' },
      failed: { cls: 'bg-red-500/20 text-red-400', text: 'Failed' },
      pending: { cls: 'bg-yellow-500/20 text-yellow-400', text: 'Pending' },
    };
    const badge = badges[s] || badges.pending;
    return (
      <span className={`px-2 py-0.5 rounded-full ${badge.cls} text-xs font-medium flex-shrink-0 flex items-center gap-1`}>
        {badge.spin && <Loader2 className="w-3 h-3 animate-spin" />}
        {badge.text}
      </span>
    );
  };

  const tabs = [
    { id: 'summary', label: 'Summary', icon: FileText },
    { id: 'actions', label: 'Actions', icon: CheckSquare, count: meetingTasks.length },
    { id: 'risks', label: 'Risks', icon: AlertTriangle, count: meetingRisks.length },
    { id: 'decisions', label: 'Decisions', icon: Lightbulb, count: (selectedMeeting?.decisions || []).length },
    { id: 'transcript', label: 'Transcript', icon: FileText },
  ];

  // Determine processing overlay content
  const getProcessingOverlay = () => {
    if (isTextProcessing) {
      return {
        title: 'AI Processing Meeting',
        subtitle: textProcessingSteps[processingStep],
        steps: textProcessingSteps,
        currentStep: processingStep,
      };
    }
    const status = selectedMeeting?.status || 'uploading';
    const stepInfo = audioProcessingSteps[status] || audioProcessingSteps.uploading;
    const allSteps = ['Uploading audio', 'Transcribing with Whisper', 'Analyzing with GPT', 'Complete'];
    return {
      title: 'Audio Processing Pipeline',
      subtitle: stepInfo.label,
      steps: allSteps,
      currentStep: stepInfo.step,
    };
  };

  return (
    <>
    <div className="h-[calc(100vh-8rem)] flex gap-6" data-testid="meetings-screen">
      {/* Meeting List */}
      <div className="w-96 flex-shrink-0 flex flex-col rounded-2xl bg-white/5 backdrop-blur-xl border border-white/10 overflow-hidden">
        <div className="p-4 border-b border-white/10">
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search meetings..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full h-10 pl-10 pr-4 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500/50"
              data-testid="meetings-search-input"
            />
          </div>
          <div className="flex items-center gap-2">
            <button className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 text-gray-400 hover:text-white hover:bg-white/10 transition-colors">
              <Filter className="w-4 h-4" />
              <span className="text-sm">Filter</span>
            </button>
          </div>

          {/* Demo: Simulate Ingestion */}
          {isDemo && (
            <div className="mt-3 p-3 rounded-xl bg-gradient-to-br from-indigo-500/10 to-cyan-500/10 border border-indigo-500/20" data-testid="simulate-ingestion-panel">
              {simulating ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 text-indigo-400 animate-spin" />
                    <span className="text-xs font-medium text-indigo-300">Simulating Ingestion</span>
                  </div>
                  <p className="text-xs text-gray-400">{stageLabels[simStage] || simStage}</p>
                  <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
                    <motion.div
                      className="h-full bg-gradient-to-r from-indigo-500 to-cyan-500"
                      initial={{ width: '10%' }}
                      animate={{ width: simStage === 'ingesting' ? '25%' : simStage === 'transcribing' ? '55%' : simStage === 'analyzing' ? '80%' : simStage === 'processed' ? '100%' : '10%' }}
                      transition={{ duration: 0.5 }}
                    />
                  </div>
                </div>
              ) : (
                <>
                  <p className="text-xs font-medium text-gray-300 mb-2">Simulate Auto-Ingestion</p>
                  <div className="grid grid-cols-2 gap-1.5">
                    {simSources.map(s => (
                      <button
                        key={s.id}
                        onClick={() => startSimulation(s.id)}
                        className={`flex items-center gap-1.5 px-2.5 py-2 rounded-lg text-xs font-medium transition-all bg-white/5 hover:bg-white/10 text-gray-300 hover:text-white border border-transparent hover:border-white/10`}
                        data-testid={`simulate-${s.id}-btn`}
                      >
                        <s.icon className="w-3.5 h-3.5" />
                        {s.label}
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={startPipelineLive}
                    className="mt-2 w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl text-xs font-semibold bg-gradient-to-r from-indigo-500/20 to-cyan-500/20 border border-indigo-500/30 text-indigo-300 hover:from-indigo-500/30 hover:to-cyan-500/30 transition-all"
                    data-testid="watch-pipeline-live-btn"
                  >
                    <Sparkles className="w-3.5 h-3.5" />
                    Watch Pipeline Live
                  </button>
                </>
              )}
            </div>
          )}
        </div>
        <div className="flex-1 overflow-y-auto p-3 space-y-2">
          {filteredMeetings.map((meeting) => (
            <motion.button
              key={meeting.id}
              onClick={() => handleMeetingClick(meeting)}
              className={`w-full text-left p-4 rounded-xl transition-all duration-200 ${
                selectedMeeting?.id === meeting.id
                  ? 'bg-gradient-to-r from-blue-500/20 to-cyan-500/20 border border-blue-500/30'
                  : 'bg-white/5 border border-transparent hover:bg-white/10 hover:border-white/10'
              }`}
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
              data-testid={`meeting-item-${meeting.id}`}
            >
              <div className="flex items-start justify-between mb-2">
                <h3 className="font-medium text-white truncate pr-2">{meeting.title}</h3>
                {getStatusBadge(meeting)}
              </div>
              <div className="flex items-center gap-4 text-xs text-gray-400">
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {meeting.date}
                </span>
                <span className="flex items-center gap-1">
                  <Users className="w-3 h-3" />
                  {meeting.participants}
                </span>
                {meeting.audioFile && (
                  <span className="flex items-center gap-1 text-cyan-400">
                    <Mic className="w-3 h-3" />
                    Audio
                  </span>
                )}
              </div>
              {meeting.processed && (
                <div className="flex items-center gap-3 mt-3">
                  <span className="px-2 py-1 rounded-md bg-blue-500/10 text-blue-400 text-xs">
                    {getTasksForMeeting(meeting.id).length} actions
                  </span>
                  {getRisksForMeeting(meeting.id).length > 0 && (
                    <span className="px-2 py-1 rounded-md bg-red-500/10 text-red-400 text-xs">
                      {getRisksForMeeting(meeting.id).length} risks
                    </span>
                  )}
                </div>
              )}
            </motion.button>
          ))}
        </div>
      </div>

      {/* Meeting Detail */}
      <div className="flex-1 flex flex-col rounded-2xl bg-white/5 backdrop-blur-xl border border-white/10 overflow-hidden relative">
        {/* Processing Overlay */}
        <AnimatePresence>
          {isProcessing && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 z-20 bg-[#0a0e1a]/80 backdrop-blur-sm flex items-center justify-center rounded-2xl"
              data-testid="ai-processing-overlay"
            >
              {(() => {
                const overlay = getProcessingOverlay();
                return (
                  <div className="text-center max-w-sm">
                    <div className="relative mb-6">
                      <div className="w-20 h-20 rounded-full border-4 border-blue-500/30 border-t-blue-500 animate-spin mx-auto"></div>
                      {isTextProcessing ? (
                        <Brain className="w-8 h-8 text-blue-400 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
                      ) : (
                        <Mic className="w-8 h-8 text-cyan-400 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
                      )}
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2">{overlay.title}</h3>
                    <motion.p
                      key={overlay.subtitle}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="text-blue-400 text-sm mb-4"
                    >
                      {overlay.subtitle}
                    </motion.p>
                    {/* Step indicators */}
                    <div className="flex items-center justify-center gap-2 mt-2">
                      {overlay.steps.map((step, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <div className={`w-2.5 h-2.5 rounded-full transition-colors ${
                            i <= overlay.currentStep ? 'bg-blue-400' : 'bg-white/20'
                          }`} />
                          {i < overlay.steps.length - 1 && (
                            <div className={`w-6 h-0.5 transition-colors ${
                              i < overlay.currentStep ? 'bg-blue-400/50' : 'bg-white/10'
                            }`} />
                          )}
                        </div>
                      ))}
                    </div>
                    <div className="flex justify-between mt-2 px-1">
                      {overlay.steps.map((step, i) => (
                        <span key={i} className={`text-[10px] max-w-[70px] ${
                          i <= overlay.currentStep ? 'text-blue-400/80' : 'text-white/30'
                        }`}>
                          {typeof step === 'string' ? step.split(' ').slice(0, 2).join(' ') : ''}
                        </span>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Header */}
        <div className="p-5 border-b border-white/10">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className="text-xl font-bold text-white mb-1">{selectedMeeting?.title}</h2>
              <div className="flex items-center gap-4 text-sm text-gray-400">
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {selectedMeeting?.date}
                </span>
                <span className="flex items-center gap-1">
                  <Video className="w-4 h-4" />
                  {selectedMeeting?.duration}
                </span>
                <span className="flex items-center gap-1">
                  <Users className="w-4 h-4" />
                  {selectedMeeting?.participants} participants
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {!selectedMeeting?.processed && !isProcessing && !['uploading','transcribing','analyzing'].includes(selectedMeeting?.status) && perms.canProcessMeeting && (
                inputMode === 'text' && transcriptInput ? (
                  <button 
                    onClick={handleProcess}
                    className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-blue-500 to-cyan-500 text-white text-sm font-medium hover:opacity-90 transition-opacity"
                    data-testid="process-meeting-btn"
                  >
                    <Sparkles className="w-4 h-4" />
                    Analyze with GPT
                  </button>
                ) : inputMode === 'audio' && selectedFile ? (
                  <button 
                    onClick={handleAudioUpload}
                    className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-500 text-white text-sm font-medium hover:opacity-90 transition-opacity"
                    data-testid="upload-audio-btn"
                  >
                    <Upload className="w-4 h-4" />
                    Transcribe & Analyze
                  </button>
                ) : null
              )}
              {selectedMeeting?.processed && !isProcessing && perms.canProcessMeeting && (
                <div className="flex items-center gap-2">
                  {selectedMeeting?.audioFile && (
                    <button 
                      onClick={handleRetranscribe}
                      className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-gray-300 text-sm font-medium hover:bg-white/10 transition-colors"
                      data-testid="retranscribe-btn"
                    >
                      <Mic className="w-4 h-4" />
                      Re-transcribe
                    </button>
                  )}
                  <button 
                    onClick={handleProcess}
                    className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-gray-300 text-sm font-medium hover:bg-white/10 transition-colors"
                    data-testid="reprocess-meeting-btn"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Re-analyze
                  </button>
                </div>
              )}
              {selectedMeeting?.status === 'failed' && !isProcessing && perms.canProcessMeeting && (
                <div className="flex items-center gap-2">
                  {selectedMeeting?.audioFile && (
                    <button 
                      onClick={handleRetranscribe}
                      className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-500 text-white text-sm font-medium hover:opacity-90 transition-opacity"
                      data-testid="retry-transcribe-btn"
                    >
                      <RefreshCw className="w-4 h-4" />
                      Retry
                    </button>
                  )}
                </div>
              )}
              <button className="p-2.5 rounded-xl bg-white/5 hover:bg-white/10 transition-colors">
                <Download className="w-5 h-5 text-gray-400" />
              </button>
              <button className="p-2.5 rounded-xl bg-white/5 hover:bg-white/10 transition-colors">
                <Share2 className="w-5 h-5 text-gray-400" />
              </button>
            </div>
          </div>

          {/* AI Badges */}
          {selectedMeeting?.processed && (
            <div className="flex items-center gap-2 flex-wrap">
              {selectedMeeting?.audioFile && (
                <span className="px-3 py-1.5 rounded-full bg-cyan-500/10 text-cyan-400 text-xs font-medium border border-cyan-500/20 flex items-center gap-1.5">
                  <Mic className="w-3.5 h-3.5" />
                  Whisper Transcribed
                </span>
              )}
              {selectedMeeting?.aiAnalysis ? (
                <span className="px-3 py-1.5 rounded-full bg-emerald-500/10 text-emerald-400 text-xs font-medium border border-emerald-500/20 flex items-center gap-1.5">
                  <Sparkles className="w-3.5 h-3.5" />
                  GPT-Powered Analysis
                </span>
              ) : (
                <span className="px-3 py-1.5 rounded-full bg-blue-500/10 text-blue-400 text-xs font-medium border border-blue-500/20 flex items-center gap-1.5">
                  <Brain className="w-3.5 h-3.5" />
                  Template-Based Analysis
                </span>
              )}
              <span className="px-3 py-1.5 rounded-full bg-purple-500/10 text-purple-400 text-xs font-medium border border-purple-500/20 flex items-center gap-1.5">
                <Zap className="w-3.5 h-3.5" />
                Auto-Extracted Actions
              </span>
              <span className="px-3 py-1.5 rounded-full bg-orange-500/10 text-orange-400 text-xs font-medium border border-orange-500/20 flex items-center gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5" />
                Real-Time Risk Detection
              </span>
            </div>
          )}

          {/* Error banner */}
          {selectedMeeting?.status === 'failed' && selectedMeeting?.processingError && (
            <div className="mt-3 p-3 rounded-xl bg-red-500/10 border border-red-500/20 flex items-start gap-3">
              <XCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-red-400 font-medium">Processing Failed</p>
                <p className="text-xs text-red-400/70 mt-1">{selectedMeeting.processingError}</p>
              </div>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 px-5 py-3 border-b border-white/10">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'bg-blue-500/20 text-blue-400'
                  : 'text-gray-400 hover:text-white hover:bg-white/5'
              }`}
              data-testid={`meeting-tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
              {tab.count !== undefined && (
                <span className={`px-1.5 py-0.5 rounded text-xs ${
                  activeTab === tab.id ? 'bg-blue-500/30' : 'bg-white/10'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-y-auto p-5">
          {!selectedMeeting?.processed && !isProcessing && selectedMeeting?.status !== 'failed' ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500/10 to-cyan-500/10 flex items-center justify-center mb-4">
                <Brain className="w-8 h-8 text-blue-400" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Meeting Not Yet Processed</h3>

              {!perms.canProcessMeeting ? (
                <div className="max-w-md">
                  <p className="text-gray-400 text-sm mb-4">
                    This meeting hasn't been analyzed yet. A Manager or Admin can upload audio or paste a transcript to process it.
                  </p>
                  <div className="flex items-center justify-center gap-2 text-gray-500 text-sm">
                    <Lock className="w-4 h-4" />
                    <span>Insufficient permissions to process meetings</span>
                  </div>
                </div>
              ) : (
                <>
              <p className="text-gray-400 text-sm mb-6 max-w-md">
                Upload an audio file for automatic transcription, or paste a transcript for GPT-powered analysis.
              </p>

              {/* Input mode toggle */}
              <div className="flex items-center gap-1 p-1 rounded-xl bg-white/5 border border-white/10 mb-6">
                <button
                  onClick={() => setInputMode('audio')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    inputMode === 'audio' ? 'bg-cyan-500/20 text-cyan-400' : 'text-gray-400 hover:text-white'
                  }`}
                  data-testid="input-mode-audio"
                >
                  <Mic className="w-4 h-4" />
                  Upload Audio
                </button>
                <button
                  onClick={() => setInputMode('text')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    inputMode === 'text' ? 'bg-blue-500/20 text-blue-400' : 'text-gray-400 hover:text-white'
                  }`}
                  data-testid="input-mode-text"
                >
                  <ClipboardPaste className="w-4 h-4" />
                  Paste Transcript
                </button>
              </div>

              {inputMode === 'audio' ? (
                <div className="w-full max-w-lg">
                  {/* Drop zone */}
                  <div
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                    className={`relative cursor-pointer rounded-2xl border-2 border-dashed p-8 transition-all duration-200 ${
                      dragActive
                        ? 'border-cyan-400 bg-cyan-500/10'
                        : selectedFile
                        ? 'border-green-500/30 bg-green-500/5'
                        : 'border-white/20 bg-white/5 hover:border-cyan-500/30 hover:bg-white/8'
                    }`}
                    data-testid="audio-drop-zone"
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".mp3,.mp4,.mpeg,.mpga,.m4a,.wav,.webm"
                      onChange={handleFileInputChange}
                      className="hidden"
                      data-testid="audio-file-input"
                    />
                    {selectedFile ? (
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-cyan-500/10 flex items-center justify-center flex-shrink-0">
                          <FileAudio className="w-6 h-6 text-cyan-400" />
                        </div>
                        <div className="flex-1 text-left">
                          <p className="text-white font-medium text-sm truncate">{selectedFile.name}</p>
                          <p className="text-gray-400 text-xs">{formatFileSize(selectedFile.size)}</p>
                        </div>
                        <button
                          onClick={(e) => { e.stopPropagation(); setSelectedFile(null); setFileError(''); }}
                          className="p-1.5 rounded-lg hover:bg-white/10 transition-colors"
                          data-testid="remove-file-btn"
                        >
                          <X className="w-4 h-4 text-gray-400" />
                        </button>
                      </div>
                    ) : (
                      <div className="text-center">
                        <div className="w-14 h-14 rounded-2xl bg-white/5 flex items-center justify-center mx-auto mb-4">
                          <Upload className="w-7 h-7 text-gray-400" />
                        </div>
                        <p className="text-white font-medium mb-1">Drop audio file here or click to browse</p>
                        <p className="text-gray-500 text-xs">
                          Supports MP3, WAV, M4A, MP4, WebM (max 25MB)
                        </p>
                      </div>
                    )}
                  </div>

                  {fileError && (
                    <div className="mt-3 flex items-center gap-2 text-red-400 text-sm" data-testid="file-error">
                      <XCircle className="w-4 h-4" />
                      {fileError}
                    </div>
                  )}

                  {selectedFile && (
                    <button 
                      onClick={handleAudioUpload}
                      disabled={isProcessing}
                      className="mt-4 w-full flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
                      data-testid="upload-and-analyze-btn"
                    >
                      <Mic className="w-5 h-5" />
                      Transcribe & Analyze with AI
                    </button>
                  )}
                </div>
              ) : (
                <div className="w-full max-w-lg">
                  <textarea
                    value={transcriptInput}
                    onChange={(e) => setTranscriptInput(e.target.value)}
                    placeholder="Paste meeting transcript here for GPT-powered analysis...&#10;&#10;Example:&#10;Sarah: Let's discuss the Q4 budget allocation.&#10;Mike: I think we need to increase the AI infrastructure budget by 15%.&#10;Jennifer: There's a compliance risk with the current timeline..."
                    rows={6}
                    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500/50 resize-none text-sm"
                    data-testid="transcript-input"
                  />
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs text-gray-500">
                      {transcriptInput ? `${transcriptInput.length} characters` : 'Optional — leave empty for template-based analysis'}
                    </span>
                    {transcriptInput && (
                      <span className="px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-400 text-xs flex items-center gap-1">
                        <Sparkles className="w-3 h-3" />
                        GPT will analyze this transcript
                      </span>
                    )}
                  </div>
                  <button 
                    onClick={handleProcess}
                    disabled={isProcessing}
                    className="mt-4 w-full flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
                    data-testid="process-meeting-cta-btn"
                  >
                    <Sparkles className="w-5 h-5" />
                    {transcriptInput ? 'Analyze with GPT' : 'Process with AI'}
                  </button>
                </div>
              )}
                </>
              )}
            </div>
          ) : (
            <>
              {activeTab === 'summary' && (
                <div className="space-y-4">
                  {/* Audio info card */}
                  {selectedMeeting?.audioFile && (
                    <div className="p-4 rounded-xl bg-cyan-500/5 border border-cyan-500/15 flex items-center gap-4">
                      <div className="w-10 h-10 rounded-xl bg-cyan-500/10 flex items-center justify-center flex-shrink-0">
                        <FileAudio className="w-5 h-5 text-cyan-400" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-white font-medium">
                          {selectedMeeting.audioFile.originalName || selectedMeeting.audioFile.filename}
                        </p>
                        <div className="flex items-center gap-3 text-xs text-gray-400 mt-0.5">
                          {selectedMeeting.audioFile.fileSize && (
                            <span>{formatFileSize(selectedMeeting.audioFile.fileSize)}</span>
                          )}
                          {selectedMeeting.audioFile.format && (
                            <span className="uppercase">{selectedMeeting.audioFile.format}</span>
                          )}
                          {selectedMeeting.transcriptMeta?.duration && (
                            <span>{Math.round(selectedMeeting.transcriptMeta.duration)}s duration</span>
                          )}
                          {selectedMeeting.transcriptMeta?.language && (
                            <span>Language: {selectedMeeting.transcriptMeta.language}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="p-5 rounded-xl bg-white/5 border border-white/10">
                    <h3 className="text-sm font-medium text-gray-400 mb-3">Meeting Summary</h3>
                    <p className="text-white leading-relaxed">{selectedMeeting?.summary || 'Processing...'}</p>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="p-4 rounded-xl bg-blue-500/10 border border-blue-500/20">
                      <p className="text-2xl font-bold text-blue-400" data-testid="meeting-action-count">{meetingTasks.length}</p>
                      <p className="text-sm text-gray-400">Action Items</p>
                    </div>
                    <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20">
                      <p className="text-2xl font-bold text-red-400" data-testid="meeting-risk-count">{meetingRisks.length}</p>
                      <p className="text-sm text-gray-400">Risks Identified</p>
                    </div>
                    <div className="p-4 rounded-xl bg-green-500/10 border border-green-500/20">
                      <p className="text-2xl font-bold text-green-400">{selectedMeeting?.participants}</p>
                      <p className="text-sm text-gray-400">Participants</p>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'actions' && (
                <div className="space-y-3">
                  {meetingTasks.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <CheckSquare className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">No actions extracted yet</p>
                    </div>
                  ) : (
                    meetingTasks.map((task) => (
                      <motion.div 
                        key={task.id} 
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="p-4 rounded-xl bg-white/5 border border-white/10 hover:border-white/20 transition-colors"
                        data-testid={`meeting-task-${task.id}`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <p className="text-white font-medium">{task.title}</p>
                          <span className={`px-2 py-1 rounded-md text-xs font-medium ${
                            task.priority === 'high' ? 'bg-red-500/20 text-red-400' :
                            task.priority === 'medium' ? 'bg-yellow-500/20 text-yellow-400' :
                            'bg-green-500/20 text-green-400'
                          }`}>
                            {task.priority}
                          </span>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-gray-400">
                          <span>Owner: {task.owner}</span>
                          <span>Due: {task.dueDate}</span>
                          <span className="px-2 py-0.5 rounded-full text-xs bg-gray-500/20 text-gray-400">
                            {task.column}
                          </span>
                        </div>
                      </motion.div>
                    ))
                  )}
                </div>
              )}

              {activeTab === 'risks' && (
                <div className="space-y-3">
                  {meetingRisks.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <AlertTriangle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">No risks detected</p>
                    </div>
                  ) : (
                    meetingRisks.map((risk) => (
                      <motion.div 
                        key={risk.id} 
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="p-4 rounded-xl bg-white/5 border border-white/10 hover:border-white/20 transition-colors"
                        data-testid={`meeting-risk-${risk.id}`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <p className="text-white font-medium">{risk.title}</p>
                          <span className={`px-2 py-1 rounded-md text-xs font-medium ${
                            risk.severity === 'high' ? 'bg-red-500/20 text-red-400' :
                            risk.severity === 'medium' ? 'bg-yellow-500/20 text-yellow-400' :
                            'bg-green-500/20 text-green-400'
                          }`}>
                            {risk.severity}
                          </span>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-gray-400">
                          <span>Owner: {risk.owner}</span>
                          <span className={`px-2 py-0.5 rounded-full text-xs ${
                            risk.status === 'mitigating' ? 'bg-orange-500/20 text-orange-400' : 'bg-blue-500/20 text-blue-400'
                          }`}>
                            {risk.status}
                          </span>
                        </div>
                      </motion.div>
                    ))
                  )}
                </div>
              )}

              {activeTab === 'transcript' && (
                <div className="space-y-4">
                  {selectedMeeting?.transcript ? (
                    <div className="p-5 rounded-xl bg-white/5 border border-white/10">
                      <h3 className="text-sm font-medium text-gray-400 mb-3 flex items-center gap-2">
                        {selectedMeeting?.audioFile ? (
                          <><Mic className="w-4 h-4 text-cyan-400" /> Whisper Transcription</>
                        ) : (
                          <><ClipboardPaste className="w-4 h-4" /> Meeting Transcript</>
                        )}
                      </h3>
                      {selectedMeeting.transcriptMeta?.segments?.length > 0 ? (
                        <div className="space-y-3">
                          {selectedMeeting.transcriptMeta.segments.map((seg, i) => (
                            <div key={i} className="flex gap-3">
                              <span className="text-xs text-cyan-400/60 w-14 flex-shrink-0 pt-0.5 font-mono">
                                {Math.floor(seg.start / 60)}:{String(Math.floor(seg.start % 60)).padStart(2, '0')}
                              </span>
                              <p className="text-gray-300 text-sm leading-relaxed">{seg.text}</p>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-gray-300 whitespace-pre-wrap text-sm leading-relaxed">{selectedMeeting.transcript}</p>
                      )}
                    </div>
                  ) : (
                    <>
                      {sampleTranscript.map((entry, index) => (
                        <div key={index} className="flex gap-4">
                          <span className="text-xs text-gray-500 w-12 flex-shrink-0 pt-1">{entry.time}</span>
                          <div className="flex-1">
                            <p className="text-sm font-medium text-blue-400 mb-1">{entry.speaker}</p>
                            <p className="text-gray-300">{entry.text}</p>
                          </div>
                        </div>
                      ))}
                      <p className="text-xs text-gray-500 text-center mt-4">Sample transcript — upload audio or paste your own transcript</p>
                    </>
                  )}
                </div>
              )}

              {activeTab === 'decisions' && (
                <div className="space-y-3">
                  {(selectedMeeting?.decisions || []).length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <Lightbulb className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">No decisions extracted yet</p>
                    </div>
                  ) : (
                    (selectedMeeting.decisions).map((decision, index) => (
                      <motion.div 
                        key={index} 
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="p-4 rounded-xl bg-white/5 border border-white/10 hover:border-white/20 transition-colors"
                        data-testid={`meeting-decision-${index}`}
                      >
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 rounded-lg bg-purple-500/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <Lightbulb className="w-4 h-4 text-purple-400" />
                          </div>
                          <div>
                            <p className="text-white font-medium mb-1">{decision.title}</p>
                            {decision.context && (
                              <p className="text-sm text-gray-400">{decision.context}</p>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    ))
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
    {/* Pipeline Live View overlay */}
    <AnimatePresence>
      {showPipeline && (
        <PipelineLiveView
          meetingId={pipelineMeetingId}
          onClose={handlePipelineClose}
          onComplete={handlePipelineComplete}
        />
      )}
    </AnimatePresence>
    </>
  );
};
